
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { authService } from "@/services/authService";
import { RoleSelector } from "./RoleSelector";
import { fetchAllRoleRecords, upsertRolesForUser } from "@/services/adminRoleService";

interface UserRole {
  role: string;
}

// Database user schema
interface UserDB {
  id: string;
  email: string;
  role: string;
  created_at: string;
  updated_at: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  status: string;
  created_at: string;
  roles?: string[];
}

const UsersModule = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      const { records } = await fetchAllRoleRecords();
      const roleMap = new Map<string, string[]>();
      records.forEach(record => {
        const existing = roleMap.get(record.user_id) ?? [];
        roleMap.set(record.user_id, [...existing, record.role]);
      });

      const usersWithRoles = (data || []).map((dbUser: UserDB) => {
        const userRoles = roleMap.get(dbUser.id) ?? (dbUser.role ? [dbUser.role] : []);

        const user: User = {
          id: dbUser.id,
          name: dbUser.email.split('@')[0],
          email: dbUser.email,
          status: 'active',
          created_at: dbUser.created_at,
          roles: userRoles,
        };

        return user;
      });

      setUsers(usersWithRoles);
    } catch (error: any) {
      toast.error(`Erro ao carregar usuários: ${error.message}`);
      console.error("Fetch users error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!newUser.name || !newUser.email || !newUser.password) {
        toast.error("Por favor, preencha todos os campos");
        return;
      }

      const result = await authService.register({
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success("Usuário criado com sucesso");
      setNewUser({ name: "", email: "", password: "" });
      setIsAddUserDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(`Erro ao criar usuário: ${error.message}`);
      console.error("Add user error:", error);
    }
  };

  const toggleUserStatus = async (user: User) => {
    try {
      const newStatus = user.status === "active" ? "inactive" : "active";
      
      // Note: The database users table doesn't have a status field
      // This is a UI-only feature for now
      // You may want to add a status column to the users table in the future
      
      toast.info("Status do usuário atualizado (apenas visual - adicione coluna 'status' ao banco para persistir)");
      
      // Update local state
      setUsers(users.map(u => 
        u.id === user.id ? { ...u, status: newStatus } : u
      ));
    } catch (error: any) {
      toast.error(`Erro ao atualizar status do usuário: ${error.message}`);
      console.error("Toggle user status error:", error);
    }
  };

  const handleRolesChange = async (userId: string, newRoles: string[]) => {
    try {
      // First, delete all existing roles for the user
      const targetUser = users.find(item => item.id === userId);
      if (!targetUser?.email) {
        throw new Error("Não foi possível determinar o email do usuário para atualizar permissões.");
      }

      await upsertRolesForUser(userId, targetUser.email, newRoles);

      toast.success("Permissões atualizadas com sucesso");
      fetchUsers();
    } catch (error: any) {
      toast.error(`Erro ao atualizar permissões: ${error.message}`);
      console.error("Update roles error:", error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gerenciamento de Usuários</h1>
        <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              <span>Adicionar Usuário</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Novo Usuário</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsAddUserDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Adicionar Usuário</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-toctoc-blue"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {users.map((user) => (
              <Card key={user.id} className={`${user.status !== "active" ? "opacity-70" : ""}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-medium">{user.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          user.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.status === "active" ? "Ativo" : "Inativo"}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Permissões:</p>
                      <RoleSelector
                        userId={user.id}
                        currentRoles={user.roles || []}
                        onRolesChange={handleRolesChange}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Criado em: {new Date(user.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleUserStatus(user)}
                      className={user.status === "active" ? "text-red-500 border-red-200" : "text-green-500 border-green-200"}
                    >
                      {user.status === "active" ? "Desativar" : "Ativar"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && users.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-500">Nenhum usuário encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersModule;
